const PP_TO_CM = { cm: 1, in: 2.54, ft: 30.48 };

if (!customElements.get('pp-colours')) {
  customElements.define(
    'pp-colours',
    class PPColours extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('[data-pp-palette]');
        this.template = this.querySelector('[data-pp-palette-content]');
        this.swatches = new Map([...this.querySelectorAll('[data-pp-layer]')].map((b) => [Number(b.dataset.ppLayer), b]));
        this.paletteNames = new Map(
          [...this.template.content.querySelectorAll('.pp-palette__colour')].map((b) => [b.dataset.hex, b.dataset.name])
        );

        this.original = new Map([...this.swatches].map(([layer, b]) => [layer, b.dataset.ppHex]));
        this.current = new Map(this.original);
        this.images = new Map([...this.swatches].filter(([layer]) => layer > 0).map(([layer, b]) => [layer, b.dataset.ppImage]));
        this.querySelectorAll('[data-pp-static]').forEach((el) => this.images.set(Number(el.dataset.ppStatic), el.dataset.ppImage));
        this.urls = new Map(this.images);
        this.originalNames = new Map([...this.swatches.keys()].map((i) => [i, this.nameEl(i).textContent]));
        this.properties = new Map([...this.swatches.keys()].map((i) => [i, 'Unchanged']));
        this.pixels = new Map();
        this.tokens = new Map();

        this.addEventListener('click', (e) => this.onClick(e));
        this.dialog.addEventListener('click', (e) => e.target === this.dialog && this.dialog.close());
        this.preselect();
      }

      nameEl(i) {
        return this.querySelector(`[data-pp-name="${i}"]`);
      }

      onClick(e) {
        const swatch = e.target.closest('[data-pp-layer]');
        if (swatch) return this.open(Number(swatch.dataset.ppLayer));
        if (e.target.closest('[data-pp-reset]')) return this.reset();
        if (e.target.closest('[data-pp-close]')) return this.dialog.close();
        const colour = e.target.closest('.pp-palette__colour');
        if (colour) return this.pick(this.layer, colour.dataset.hex);
      }

      open(layer) {
        if (!this.dialog.querySelector('.pp-palette__sheet')) {
          this.dialog.append(this.template.content.cloneNode(true));
          this.dialog.querySelector('[data-pp-hex]').addEventListener('submit', (e) => this.onHex(e));
        }
        this.layer = layer;
        this.images.forEach((_, l) => this.layerPixels(l));
        this.classList.add('pp-colours--opened');
        this.dialog.querySelector('[data-pp-palette-heading]').textContent = this.dataset.paletteTitle.replace(
          '[layer]',
          this.swatches.get(layer).dataset.ppTitle
        );
        this.markActive();
        const input = this.dialog.querySelector('[data-pp-hex] input');
        input.value = '';
        input.removeAttribute('aria-invalid');
        this.dialog.querySelector('.pp-palette__hex-error').hidden = true;
        this.dialog.showModal();
      }

      markActive() {
        const hex = this.current.get(this.layer);
        this.dialog.querySelectorAll('.pp-palette__colour').forEach((b) => b.classList.toggle('is-active', b.dataset.hex === hex));
      }

      onHex(e) {
        e.preventDefault();
        const input = e.currentTarget.querySelector('input');
        let value = input.value.trim();
        if (value.charAt(0) !== '#') value = `#${value}`;
        const valid = /^#[0-9A-F]{6}$/i.test(value);
        input.toggleAttribute('aria-invalid', !valid);
        this.dialog.querySelector('.pp-palette__hex-error').hidden = valid;
        if (valid) this.pick(this.layer, value);
      }

      pick(layer, hex) {
        this.setColour(layer, hex);
        if (this.dialog.open) this.dialog.close();
        this.updateUrl();
      }

      setColour(layer, value) {
        const hex = value.toLowerCase();
        const original = this.original.get(layer);
        const paletteName = this.paletteNames.get(hex);
        let label, property;
        if (paletteName) {
          label = property = paletteName;
        } else if (hex !== original) {
          label = property = value;
        } else {
          label = this.originalNames.get(layer);
          property = 'Unchanged';
        }
        this.current.set(layer, hex);
        this.properties.set(layer, property);
        this.nameEl(layer).textContent = label;

        const swatch = this.swatches.get(layer);
        swatch.style.setProperty('--pp-swatch', hex);
        const [r, g, b] = this.rgb(hex);
        swatch.classList.toggle('pp-swatch__circle--dark', r + g + b < 382);

        if (layer === 0) document.documentElement.style.setProperty('--pp-design-bg', hex);
        else this.recolour(layer, hex);
        this.updateDynamicBackgrounds();
        this.dispatchEvent(new CustomEvent('pp:colours-changed', { bubbles: true }));
      }

      rgb(hex) {
        const n = parseInt(hex.slice(1), 16);
        return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
      }

      async layerPixels(layer) {
        if (!this.pixels.has(layer)) {
          this.pixels.set(
            layer,
            new Promise((resolve, reject) => {
              const img = new Image();
              img.crossOrigin = 'anonymous';
              img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                const ctx = canvas.getContext('2d', { willReadFrequently: true });
                ctx.drawImage(img, 0, 0);
                resolve({ canvas, ctx, source: ctx.getImageData(0, 0, canvas.width, canvas.height) });
              };
              img.onerror = reject;
              img.src = this.images.get(layer);
            })
          );
        }
        return this.pixels.get(layer);
      }

      async recolour(layer, hex) {
        const token = (this.tokens.get(layer) || 0) + 1;
        this.tokens.set(layer, token);
        const { canvas, ctx, source } = await this.layerPixels(layer);
        if (this.tokens.get(layer) !== token) return;
        const isOriginal = hex === this.original.get(layer);
        if (isOriginal) {
          ctx.putImageData(source, 0, 0);
        } else {
          const [ir, ig, ib] = this.rgb(this.original.get(layer));
          const [nr, ng, nb] = this.rgb(hex);
          const dr = nr - ir;
          const dg = ng - ig;
          const db = nb - ib;
          const src = source.data;
          const out = ctx.createImageData(canvas.width, canvas.height);
          const dst = out.data;
          for (let i = 0; i < src.length; i += 4) {
            dst[i] = src[i] + dr;
            dst[i + 1] = src[i + 1] + dg;
            dst[i + 2] = src[i + 2] + db;
            dst[i + 3] = src[i + 3];
          }
          ctx.putImageData(out, 0, 0);
        }
        await this.paint();

        const previous = this.urls.get(layer);
        if (isOriginal) {
          this.urls.set(layer, this.images.get(layer));
        } else {
          const blob = await new Promise((resolve) => canvas.toBlob(resolve));
          if (this.tokens.get(layer) !== token) return;
          this.urls.set(layer, URL.createObjectURL(blob));
        }
        if (previous && previous.startsWith('blob:')) setTimeout(() => URL.revokeObjectURL(previous), 1000);
        document.documentElement.style.setProperty(
          '--pp-design',
          [...this.urls]
            .sort(([a], [b]) => b - a)
            .map(([, url]) => `url("${url}")`)
            .join(', ')
        );
      }

      async paint() {
        const root = this.closest('.pp');
        const layers = await Promise.all([...this.images.keys()].sort((a, b) => a - b).map((l) => this.layerPixels(l)));
        if (!this.tile) {
          this.tile = document.createElement('canvas');
          this.tile.width = layers[0].canvas.width;
          this.tile.height = layers[0].canvas.height;
          this.targets = [...root.querySelectorAll('.pp-design, .pp-scene__design')].map((el) => {
            const canvas = document.createElement('canvas');
            canvas.className = 'pp-live';
            el.append(canvas);
            return { el, canvas };
          });
          root.classList.add('pp--live');
          new ResizeObserver(() => this.drawTargets()).observe(root.querySelector('.pp-gallery'));
        }
        const tile = this.tile.getContext('2d');
        tile.clearRect(0, 0, this.tile.width, this.tile.height);
        layers.forEach(({ canvas }) => tile.drawImage(canvas, 0, 0, this.tile.width, this.tile.height));
        this.drawTargets();
      }

      drawTargets() {
        const root = this.closest('.pp');
        const pattern = parseFloat(getComputedStyle(root).getPropertyValue('--pp-pattern')) || 0;
        const dpr = window.devicePixelRatio || 1;
        const tile = this.tile;
        this.targets.forEach(({ el, canvas }) => {
          const w = Math.round(el.clientWidth * dpr);
          const h = Math.round(el.clientHeight * dpr);
          if (!w || !h) return;
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (el.classList.contains('pp-design')) {
            ctx.drawImage(tile, 0, 0, w, h);
            return;
          }
          const room = parseFloat(el.parentElement.style.getPropertyValue('--pp-room')) || 100;
          const scale = pattern > 0 ? (w * pattern) / room / tile.width : Math.max(w / tile.width, h / tile.height);
          const fill = ctx.createPattern(tile, 'repeat');
          fill.setTransform(new DOMMatrix().scaleSelf(scale, scale));
          ctx.fillStyle = fill;
          ctx.fillRect(0, 0, w, h);
        });
      }

      updateDynamicBackgrounds() {
        document.querySelectorAll('.dynamic-background').forEach((el) => {
          const layer = el.getAttribute('layer');
          const hex = this.current.get(layer === 'bg' ? 0 : Number(layer));
          el.style.backgroundColor = hex || el.getAttribute('color');
        });
      }

      reset() {
        this.original.forEach((hex, layer) => this.setColour(layer, hex));
        this.updateUrl(true);
      }

      updateUrl(clear) {
        const url = new URL(window.location.href);
        const changed = [...this.current].some(([layer, hex]) => hex !== this.original.get(layer));
        const params = [...url.searchParams].filter(([key]) => key !== 'layers');
        const query = params.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
        if (!clear && changed) {
          const values = [...this.swatches.keys()].map((i) => this.nameEl(i).textContent.replace('#', ''));
          query.push(`layers=${values.map((v) => encodeURIComponent(v)).join(',')}`);
        }
        history.replaceState(history.state, '', url.pathname + (query.length ? `?${query.join('&')}` : '') + url.hash);
      }

      preselect() {
        const raw = new URL(window.location.href).searchParams.get('layers');
        if (!raw) return;
        const data = JSON.parse(this.closest('.pp').querySelector('[data-pp-data]').textContent);
        const library = new Map(Object.entries(data.library).map(([name, hex]) => [name.toLowerCase(), hex]));
        const order = [...this.swatches.keys()];
        raw.split(',').forEach((value, i) => {
          const layer = order[i];
          if (layer === undefined) return;
          const name = value.trim().toLowerCase();
          const hex = library.get(name) || `#${value.trim()}`;
          if (/^#[0-9a-f]{6}$/i.test(hex)) this.setColour(layer, hex);
        });
      }

      async exportPNG() {
        const layers = await Promise.all([...this.images.keys()].sort((a, b) => a - b).map((l) => this.layerPixels(l)));
        const canvas = document.createElement('canvas');
        canvas.width = layers[0].canvas.width;
        canvas.height = layers[0].canvas.height;
        const ctx = canvas.getContext('2d');
        if (this.current.has(0)) {
          ctx.fillStyle = this.current.get(0);
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        layers.forEach((layer) => ctx.drawImage(layer.canvas, 0, 0, canvas.width, canvas.height));
        return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      }

      getSelection() {
        return [...this.swatches.entries()].map(([layer, button]) => ({
          layer,
          title: button.dataset.ppTitle,
          property: this.properties.get(layer),
        }));
      }
    }
  );
}

if (!customElements.get('pp-dimensions')) {
  customElements.define(
    'pp-dimensions',
    class PPDimensions extends HTMLElement {
      connectedCallback() {
        this.width = this.querySelector('[data-pp-width]');
        this.widthUnit = this.querySelector('[data-pp-width-unit]');
        this.height = this.querySelector('[data-pp-height]');
        this.heightUnit = this.querySelector('[data-pp-height-unit]');
        this.walls = this.querySelector('[data-pp-walls]');
        this.wallTemplate = this.querySelector('[data-pp-wall-template]');
        this.widthPlaceholder = this.width.placeholder;
        this.infoDialog = this.querySelector('[data-pp-info-dialog]');

        this.addEventListener('input', () => this.update());
        this.addEventListener('change', () => this.update());
        this.addEventListener('click', (e) => {
          if (e.target.closest('[data-pp-walls-open]')) this.openWalls();
          if (e.target.closest('[data-pp-wall-add]')) this.addWall();
          if (e.target.closest('[data-pp-wall-remove]')) this.removeWall();
          if (e.target.closest('[data-pp-info]')) this.infoDialog.showModal();
          if (e.target === this.infoDialog || e.target.closest('[data-pp-info-close]')) this.infoDialog.close();
        });
        this.addEventListener(
          'wheel',
          (e) => {
            if (e.target.type === 'number' && e.target === document.activeElement) e.target.blur();
          },
          { passive: true }
        );
        this.update();
      }

      wallRows() {
        return [...this.walls.querySelectorAll('[data-pp-wall]')];
      }

      isMulti() {
        return !this.walls.hidden;
      }

      addWall(value, unit) {
        const row = this.wallTemplate.content.firstElementChild.cloneNode(true);
        const rows = this.wallRows();
        const n = rows.length + 1;
        const id = `${this.closest('.pp').id}-wall-${n}`;
        const input = row.querySelector('input');
        const label = row.querySelector('[data-pp-wall-label]');
        label.textContent = `${this.dataset.wallShort}${n}`;
        label.htmlFor = id;
        input.id = id;
        input.placeholder = this.dataset.wallPlaceholder.replace('[n]', n);
        if (value) input.value = value;
        row.querySelector('select').value = unit || (rows.length ? rows[rows.length - 1].querySelector('select').value : this.widthUnit.value);
        this.walls.append(row);
        this.update();
        return input;
      }

      openWalls() {
        const value = parseFloat(this.width.value) > 0 ? this.width.value : '';
        this.walls.hidden = false;
        this.addWall(value, this.widthUnit.value);
        this.addWall('', this.widthUnit.value).focus();
        this.width.value = '';
        this.width.placeholder = this.dataset.multiple;
        this.width.disabled = true;
        this.widthUnit.disabled = true;
        this.toggleActions();
        this.update();
      }

      removeWall() {
        const rows = this.wallRows();
        if (rows.length > 2) {
          rows[rows.length - 1].remove();
        } else {
          this.width.disabled = false;
          this.widthUnit.disabled = false;
          this.width.value = rows[0].querySelector('input').value;
          this.widthUnit.value = rows[0].querySelector('select').value;
          this.width.placeholder = this.widthPlaceholder;
          rows.forEach((row) => row.remove());
          this.walls.hidden = true;
          this.toggleActions();
        }
        this.update();
      }

      toggleActions() {
        const multi = this.isMulti();
        this.querySelector('[data-pp-walls-open]').hidden = multi;
        this.querySelector('[data-pp-wall-add]').hidden = !multi;
        this.querySelector('[data-pp-wall-remove]').hidden = !multi;
      }

      getDimensions() {
        const walls = this.isMulti()
          ? this.wallRows().map((row) => ({ value: row.querySelector('input').value, unit: row.querySelector('select').value }))
          : [{ value: this.width.value, unit: this.widthUnit.value }];
        const heightValue = parseFloat(this.height.value) || 0;
        const heightCm = heightValue * PP_TO_CM[this.heightUnit.value];
        let status = 'empty';
        let feet = 0;
        if (heightValue > 0) {
          feet = Math.ceil((heightCm - 0.001) / 30.48);
          if (feet > 30) status = 'units';
          else if (feet > 15) status = 'too-tall';
          else {
            feet = Math.max(feet, 4);
            status = 'ok';
          }
        }
        const entered = walls.filter((w) => parseFloat(w.value) > 0);
        return {
          walls: entered,
          feet: status === 'ok' ? feet : 0,
          status: status === 'ok' && !entered.length ? 'empty' : status,
          widthText: entered.map((w) => `${w.value} ${w.unit}`).join(', '),
          heightText: `${this.height.value} ${this.heightUnit.value}`,
          heightUnit: this.heightUnit.value,
        };
      }

      update() {
        this.dimensions = this.getDimensions();
        this.querySelector('[data-pp-error="too-tall"]').hidden = this.dimensions.status !== 'too-tall';
        this.querySelector('[data-pp-error="units"]').hidden = this.dimensions.status !== 'units';
        this.dispatchEvent(new CustomEvent('pp:dimensions-changed', { bubbles: true, detail: this.dimensions }));
      }
    }
  );
}

if (!customElements.get('pp-gallery')) {
  customElements.define(
    'pp-gallery',
    class PPGallery extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('.pp-gallery');
        this.slides = [...this.list.children];
        this.dots = [...this.querySelectorAll('.pp-gallery__dot')];
        this.prev = this.querySelector('[data-pp-prev]');
        this.next = this.querySelector('[data-pp-next]');
        if (!this.dots.length) return;

        this.prev.addEventListener('click', () => this.go(this.index - 1));
        this.next.addEventListener('click', () => this.go(this.index + 1));
        this.index = 0;
        let frame = 0;
        const track = () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => this.setActive(this.currentIndex()));
        };
        this.list.addEventListener('scroll', track, { passive: true });
        this.list.addEventListener('scrollend', track);
        window.addEventListener('resize', track);
        this.setActive(this.currentIndex());
      }

      currentIndex() {
        const width = this.list.clientWidth;
        if (!width) return this.index;
        return Math.max(0, Math.min(this.slides.length - 1, Math.round(this.list.scrollLeft / width)));
      }

      go(index) {
        const target = Math.max(0, Math.min(index, this.slides.length - 1));
        this.list.scrollTo({ left: target * this.list.clientWidth, behavior: 'smooth' });
      }

      setActive(index) {
        this.index = index;
        this.dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
          dot.style.setProperty('--pp-dot-distance', Math.abs(i - index));
        });
        this.prev.disabled = index === 0;
        this.next.disabled = index === this.slides.length - 1;
      }
    }
  );
}

if (!customElements.get('pp-buy')) {
  customElements.define(
    'pp-buy',
    class PPBuy extends HTMLElement {
      connectedCallback() {
        this.root = this.closest('.pp');
        this.error = this.querySelector('[data-pp-dims-error]');
        this.querySelector('[data-pp-choose]').addEventListener('click', () => this.choose());
        this.querySelector('[data-pp-sample]').addEventListener('click', () => this.goTo(2, 'sample'));
        this.root.querySelector('[data-pp-back]')?.addEventListener('click', () => this.goTo(1));
        this.root.addEventListener('pp:dimensions-changed', () => {
          if (!this.dimsMissing()) this.error.hidden = true;
        });

        this.form = this.querySelector('form.pp-buy__form');
        this.cartError = this.querySelector('[data-pp-cart-error]');
        this.form.addEventListener('submit', (e) => this.addToCart(e));
        const sync = () => this.sync();
        this.root.addEventListener('pp:dimensions-changed', sync);
        this.root.addEventListener('pp:page-changed', sync);
        this.root.addEventListener('change', (e) => e.target.matches('.pp-material__input') && sync());
        this.sync();
      }

      quote() {
        const price = this.root.querySelector('pp-price');
        return price && price.getQuote ? price.getQuote() : { status: 'empty' };
      }

      sync() {
        const q = this.quote();
        this.querySelectorAll('.pp-buy__add').forEach((button) => (button.disabled = q.status !== 'ok' || !q.variant.a));
        if (q.status !== 'ok') return;
        [
          [this.form.querySelector('[data-pp-variant]'), q.variant.id],
          [this.form.querySelector('[data-pp-quantity]'), q.panels],
        ].forEach(([input, value]) => {
          if (input.value === String(value)) return;
          input.value = value;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      async addToCart(e) {
        e.preventDefault();
        const q = this.quote();
        if (q.status !== 'ok') return;
        const button = e.submitter || this.querySelector('.pp-buy__add:not([hidden])');
        if (button.classList.contains('is-loading')) return;
        button.classList.add('is-loading');
        this.cartError.hidden = true;

        const body = new FormData();
        body.append('id', q.variant.id);
        body.append('quantity', q.panels);
        if (!q.sample) {
          const dims = this.dims().getDimensions();
          body.append('properties[Width]', dims.widthText);
          body.append('properties[Height]', dims.heightText);
        }
        const colours = this.root.querySelector('pp-colours');
        if (colours) {
          colours.getSelection().forEach(({ layer, title, property }) => {
            body.append(`properties[${layer === 0 ? 'Background' : title}]`, property);
          });
          const png = await colours.exportPNG().catch(() => null);
          if (png) body.append('properties[Your Colors]', png, 'image.png');
        }
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        if (cart) {
          body.append('sections', cart.getSectionsToRender().map((section) => section.id));
          body.append('sections_url', window.location.pathname);
          cart.setActiveElement(document.activeElement);
        }

        try {
          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
            body,
          });
          const data = await response.json();
          if (data.status) {
            this.cartError.textContent = data.description || data.message || window.cartStrings.error;
            this.cartError.hidden = false;
            return;
          }
          if (window.Shopify && Shopify.analytics) {
            Shopify.analytics.publish('add_to_cart', {
              value: (q.total / 100).toFixed(2),
              currency: Shopify.currency.active,
              productID: Number(this.dataset.productId),
            });
          }
          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }
          cart.classList.remove('is-empty');
          cart.renderContents(data);
          if (q.sample) this.goTo(1);
        } catch (error) {
          this.cartError.textContent = window.cartStrings.error;
          this.cartError.hidden = false;
        } finally {
          button.classList.remove('is-loading');
        }
      }

      dims() {
        return this.root.querySelector('pp-dimensions');
      }

      dimsMissing() {
        const dims = this.dims();
        if (!dims) return false;
        const { walls } = dims.getDimensions();
        return !(parseFloat(dims.height.value) > 0) || !walls.length;
      }

      choose() {
        if (!this.dimsMissing()) return this.goTo(2, 'order');
        const dims = this.dims();
        this.error.hidden = false;
        dims.classList.remove('pp-dims--attention');
        void dims.offsetWidth;
        dims.classList.add('pp-dims--attention');
        setTimeout(() => dims.classList.remove('pp-dims--attention'), 3000);
        const rect = dims.getBoundingClientRect();
        const top = this.headerOffset();
        if (rect.top < top || rect.bottom > window.innerHeight) {
          window.scrollTo({ top: rect.top + window.scrollY - top - 24, behavior: 'smooth' });
        }
        const empty = [...dims.querySelectorAll('input[type="number"]')].find((input) => !input.disabled && !input.value);
        if (empty) empty.focus({ preventScroll: true });
      }

      goTo(page, mode) {
        this.root.dataset.page = page;
        this.root.dataset.mode = page === 2 ? mode : '';
        const panel = this.root.querySelector('.pp__panel');
        const top = this.headerOffset();
        const rect = panel.getBoundingClientRect();
        if (rect.top < top || rect.top > window.innerHeight * 0.6) {
          window.scrollTo({ top: rect.top + window.scrollY - top - 8, behavior: 'smooth' });
        }
        this.root.dispatchEvent(new CustomEvent('pp:page-changed', { bubbles: true, detail: { page, mode } }));
      }

      headerOffset() {
        const header = document.querySelector('sticky-header');
        if (!header) return 0;
        const rect = header.getBoundingClientRect();
        return rect.bottom > 0 ? rect.bottom : 0;
      }
    }
  );
}

const PP_PANEL = [
  { in: 18.8976, cm: 48, ft: 1.5748 },
  { in: 18.8976, cm: 48, ft: 1.5748 },
  { in: 25.27559, cm: 64.2, ft: 2.106299 },
];
const PP_PANEL_LABEL = [
  { cm: 48, in: 19 },
  { cm: 48, in: 19 },
  { cm: 64, in: 25 },
];
const PP_TRIM_CM = 5.08;

function ppMoneyFormatter(sample, wholeSample) {
  const trim = wholeSample
    ? !/[.,]\d{2}(?!\d)/.test(wholeSample)
    : (window.Shopify && Shopify.currency && Shopify.currency.active) === 'SEK';
  const m = String(sample).match(/^(.*?)(\d(?:[\d.,'\s  ]*\d)?)(.*)$/);
  if (!m) return (cents) => (cents / 100).toFixed(2);
  const [, prefix, number, suffix] = m;
  const dec = number.match(/^(.*\d)(\D)(\d+)$/);
  const hasDecimals = !!dec && dec[1].replace(/\D/g, '') === '1234';
  const intPart = hasDecimals ? dec[1] : number;
  const thousands = (intPart.match(/\D/) || [''])[0];
  const places = hasDecimals ? dec[3].length : 0;
  return (cents) => {
    const [whole, frac] = (cents / 100).toFixed(trim && cents % 100 === 0 ? 0 : places).split('.');
    return prefix + whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? dec[2] + frac : '') + suffix;
  };
}

if (!customElements.get('pp-price')) {
  customElements.define(
    'pp-price',
    class PPPrice extends HTMLElement {
      connectedCallback() {
        this.root = this.closest('.pp');
        this.data = JSON.parse(this.root.querySelector('[data-pp-data]').textContent);
        this.money = ppMoneyFormatter(this.data.money, this.data.moneyWhole);
        const update = () => this.update();
        this.root.addEventListener('pp:dimensions-changed', update);
        this.root.addEventListener('pp:page-changed', update);
        this.root.addEventListener('change', (e) => e.target.matches('.pp-material__input') && update());
        this.update();
      }

      material() {
        const checked = this.root.querySelector('.pp-material__input:checked');
        return checked ? Number(checked.value) : 0;
      }

      getQuote() {
        const material = this.material();
        if (this.root.dataset.mode === 'sample') {
          const variant = this.data.variants.find((v) => v.m === material && v.h === 0);
          return variant ? { status: 'ok', sample: true, material, variant, panels: 1, total: variant.p } : { status: 'empty' };
        }
        const dims = this.root.querySelector('pp-dimensions');
        const d = dims ? dims.getDimensions() : { status: 'empty' };
        if (d.status !== 'ok') return { status: d.status };
        const variant = this.data.variants.find((v) => v.m === material && v.h === d.feet);
        if (!variant) return { status: 'empty' };
        const panel = PP_PANEL[material];
        const needed = d.walls.reduce((sum, w) => sum + parseFloat(w.value) / panel[w.unit], 0);
        const panels = Math.ceil(needed + PP_TRIM_CM / panel.cm);
        const total = panels * variant.p;
        const discount = this.data.discount;
        return {
          status: 'ok',
          material,
          variant,
          panels,
          feet: d.feet,
          heightUnit: d.heightUnit,
          total,
          compare: variant.c > variant.p ? panels * variant.c : 0,
          codeTotal: discount ? Math.floor((total * (100 - discount.percent)) / 100) : 0,
        };
      }

      update() {
        const q = (this.quote = this.getQuote());
        this.hidden = q.status !== 'ok';
        if (q.status !== 'ok') return;
        const panels = this.querySelector('[data-pp-panels]');
        panels.hidden = !!q.sample;
        if (!q.sample) {
          const metric = q.heightUnit === 'cm';
          const label = PP_PANEL_LABEL[q.material];
          const width = metric ? `${label.cm} ${this.dataset.unitCm}` : `${label.in} ${this.dataset.unitIn}`;
          const height = metric ? `${Math.round(q.feet * 30.48)} ${this.dataset.unitCm}` : `${q.feet} ${this.dataset.unitFt}`;
          panels.textContent = (q.panels === 1 ? this.dataset.panelsOne : this.dataset.panelsOther)
            .replace('[count]', q.panels)
            .replace('[width]', width)
            .replace('[height]', height);
        }
        this.querySelector('[data-pp-now]').textContent = this.money(q.total);
        const was = this.querySelector('[data-pp-was]');
        was.textContent = q.compare ? this.money(q.compare) : '';
        was.hidden = !q.compare;
        const code = this.querySelector('[data-pp-code]');
        code.hidden = !q.codeTotal;
        code.textContent = q.codeTotal
          ? this.dataset.withCode.replace('[price]', this.money(q.codeTotal)).replace('[code]', this.data.discount.code)
          : '';
      }
    }
  );
}

if (!customElements.get('pp-help')) {
  customElements.define(
    'pp-help',
    class PPHelp extends HTMLElement {
      connectedCallback() {
        this.root = this.closest('.pp');
        this.addEventListener('click', (e) => {
          const opener = e.target.closest('[data-pp-help-open]');
          if (opener) return this.open(opener.dataset.ppHelpOpen);
          const dialog = e.target.closest('dialog');
          if (!dialog) return;
          if (e.target === dialog || e.target.closest('[data-pp-help-close]')) dialog.close();
        });
      }

      open(key) {
        const dialog = this.querySelector(`[data-pp-help="${key}"]`);
        if (!dialog) return;
        if (!dialog.querySelector('.pp-help__box')) {
          dialog.append(dialog.querySelector('[data-pp-help-content]').content.cloneNode(true));
          if (key === 'quiz') this.setupQuiz(dialog);
        }
        if (key === 'quiz') this.quizReset();
        dialog.showModal();
      }

      setupQuiz(dialog) {
        const quiz = dialog.querySelector('[data-pp-quiz]');
        const q = (this.quiz = {
          el: quiz,
          steps: [...quiz.querySelectorAll('[data-quiz-step]')],
          cards: [...quiz.querySelectorAll('[data-quiz-card]')],
          progress: dialog.querySelector('[data-quiz-progress]'),
          back: quiz.querySelector('[data-quiz-back]'),
          restart: quiz.querySelector('[data-quiz-restart]'),
          history: [],
          state: 'q1',
          keys: [],
          chosen: false,
          timer: null,
        });

        quiz.addEventListener('click', (e) => {
          const opt = e.target.closest('.pp-quiz__opt');
          if (opt) {
            if (q.timer) return;
            opt.parentNode.querySelectorAll('.pp-quiz__opt').forEach((o) => o.setAttribute('aria-checked', String(o === opt)));
            const go = opt.dataset.quizGo;
            const toResult = go.startsWith('result');
            setTimeout(() => quiz.classList.add('is-leaving'), toResult ? 150 : 90);
            q.timer = setTimeout(() => {
              q.timer = null;
              q.history.push(q.state);
              this.quizRender(go);
            }, toResult ? 310 : 220);
            return;
          }
          if (e.target.closest('[data-quiz-back]')) {
            this.quizStop();
            if (q.history.length) this.quizRender(q.history.pop());
            return;
          }
          if (e.target.closest('[data-quiz-restart]')) return this.quizReset();
          const select = e.target.closest('[data-quiz-select]');
          if (select) {
            const card = select.closest('[data-quiz-card]');
            q.cards.forEach((c) => c.classList.toggle('is-chosen', c === card));
            q.chosen = true;
            this.selectMaterial(card);
            dialog.close();
          }
        });

        dialog.addEventListener('close', () => {
          if (q.keys.length && !q.chosen) {
            const card = q.cards.find((c) => c.dataset.quizCard === q.keys[0]);
            if (card) this.selectMaterial(card);
          }
          q.keys = [];
        });
      }

      quizStop() {
        clearTimeout(this.quiz.timer);
        this.quiz.timer = null;
        this.quiz.el.classList.remove('is-leaving');
      }

      quizReset() {
        const q = this.quiz;
        if (!q) return;
        this.quizStop();
        q.history = [];
        q.keys = [];
        q.chosen = false;
        this.quizRender('q1');
      }

      quizRender(id) {
        const q = this.quiz;
        const [step, keyList] = id.split(':');
        const keys = keyList ? keyList.split(',') : [];
        q.state = id;
        q.el.classList.remove('is-leaving');
        q.steps.forEach((s) => {
          const on = s.dataset.quizStep === step;
          s.hidden = !on;
          s.classList.remove('is-entering');
          if (on) {
            void s.offsetWidth;
            s.classList.add('is-entering');
            s.querySelectorAll('.pp-quiz__opt').forEach((o) => o.setAttribute('aria-checked', 'false'));
          }
        });
        const isResult = step === 'result';
        if (isResult) {
          q.keys = keys;
          q.cards.forEach((c) => {
            c.hidden = !keys.includes(c.dataset.quizCard);
            c.classList.remove('is-chosen');
          });
          const shown = q.cards.filter((c) => !c.hidden);
          q.el.querySelector('[data-quiz-results]').classList.toggle('is-pair', shown.length > 1);
          const text = q.el.querySelector(`[data-quiz-text="${keys.join(',')}"]`);
          const intro = q.el.querySelector('[data-quiz-intro]');
          intro.textContent = text ? text.textContent : '';
          intro.hidden = !intro.textContent;
          const d = q.el.dataset;
          q.el.querySelector('[data-quiz-result-title]').textContent = [d.prefix, shown.map((c) => c.dataset.quizTitle).join(` ${d.or} `), d.suffix]
            .filter(Boolean)
            .join(' ');
        }
        if (q.progress) q.progress.style.width = isResult ? '100%' : `${Math.round((q.history.length / 3) * 100)}%`;
        q.el.closest('dialog').classList.toggle('is-done', isResult);
        q.back.disabled = !q.history.length;
        q.restart.hidden = !isResult;
        const box = q.el.closest('.pp-help__box');
        if (box) box.scrollTop = 0;
      }

      selectMaterial(card) {
        const input = this.root && this.root.querySelector(`.pp-material__input[value="${card.dataset.quizPosition}"]`);
        if (input && !input.checked) input.click();
      }
    }
  );
}